import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
// import { MOCK_TEMPLATES } from "@/data/mock-data";
import { Template } from "@/types";
import TemplateCard from "./TemplateCard";
import TemplateCardSkeleton from "./TemplateCardSkeleton";
import TemplatePreviewDialog from "./TemplatePreviewDialog";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

import { useTranslation } from "react-i18next";

interface TemplateSelectorProps {
  onSelect: (template: Template) => void;
  initialTemplateId?: string;
}

const TemplateSelector = ({ onSelect, initialTemplateId }: TemplateSelectorProps) => {
  const { i18n } = useTranslation();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(initialTemplateId);
  const [isLoading, setIsLoading] = useState(true);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [templateToPreview, setTemplateToPreview] = useState<Template | null>(null);
  const navigate = useNavigate();

  // Helper function to ensure template IDs are valid UUIDs
  const ensureValidUuid = (templateId: string): string => {
    // If it's already in UUID format, return it
    if (
      templateId &&
      typeof templateId === "string" &&
      templateId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
    ) {
      return templateId;
    }
    // Otherwise, generate a new UUID
    return crypto.randomUUID();
  };

  useEffect(() => {
    // Fetch templates from Supabase if available, otherwise use mock data
    const fetchTemplates = async () => {
      try {
        setIsLoading(true);
        const currentLang = i18n.language.split('-')[0];
        const { data: dbTemplates, error } = await supabase
          .from("templates")
          .select("*")
          .is("deleted_at", null)
          .order("order_code", { ascending: true, nullsFirst: false })
          .order("created_at", { ascending: false });

        if (error) {
          console.error("Error fetching templates:", error);
          // Set empty array if there's an error
          setTemplates([]);
          return;
        }

        if (dbTemplates && dbTemplates.length > 0) {
          // Filter system templates by language client-side
          const filteredTemplates = dbTemplates.filter(t => !t.is_system || t.lang === currentLang);

          setTemplates(
            filteredTemplates.map((template) => ({
              id: template.id,
              name: template.name,
              description: template.description,
              prompt: template.prompt,
              preview_template_result: template.preview_template_result,
              isSystem: template.is_system,
              userId: template.user_id,
              createdAt: template.created_at,
              deletedAt: template.deleted_at,
              orderCode: template.order_code,
              lang: template.lang,
            }))
          );
        } else {
          // Set empty array if no templates found in database
          setTemplates([]);
        }
      } catch (err) {
        console.error("Error in templates fetch:", err);
        // Set empty array on error
        setTemplates([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();

    if (initialTemplateId) {
      setSelectedTemplateId(initialTemplateId);
    }
  }, [initialTemplateId]);

  const handleTemplateSelect = (template: Template) => {
    // Ensure the template has a valid UUID before passing it to the parent
    const validTemplate = {
      ...template,
      id: ensureValidUuid(template.id),
    };

    setSelectedTemplateId(validTemplate.id);
    onSelect(validTemplate);
  };

  const handleCreateTemplate = () => {
    navigate("/templates/create");
  };

  const handlePreviewClick = (template: Template) => {
    setTemplateToPreview(template);
    setPreviewDialogOpen(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-medium">Выберите шаблон</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCreateTemplate}
          disabled={isLoading}
          className="flex items-center gap-1"
        >
          <Plus size={16} />
          <span>Создать</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isLoading
          ? // Показываем скелетоны во время загрузки
          Array.from({ length: 4 }, (_, index) => <TemplateCardSkeleton key={`skeleton-${index}`} />)
          : // Показываем реальные шаблоны после загрузки
          templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              selected={selectedTemplateId === template.id}
              onClick={() => handleTemplateSelect(template)}
              onPreviewClick={handlePreviewClick}
            />
          ))}
      </div>

      {templateToPreview && (
        <TemplatePreviewDialog
          template={templateToPreview}
          open={previewDialogOpen}
          onOpenChange={setPreviewDialogOpen}
        />
      )}
    </div>
  );
};

export default TemplateSelector;
